import React, { useContext, useEffect, useState } from 'react';
import {
    CircularProgress,
    NativeSelect,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
    Tabs,
} from '@material-ui/core';

import { productionApi } from 'api/production';
import { systemApi as api } from 'api/system';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications, TabPanel } from 'shared';

function LiveInventory() {
    const [packGroups, setPackGroups] = useState([]);
    const [selectedPackGroup, setSelectedPackGroup] = useState('');
    const [notification, setNotification] = useState({ text: null, level: null });
    const [rows, setRows] = useState([]);
    const [packDateColumns, setPackDateColumns] = useState([]);
    const [loading, setLoading] = useState(false);
    const { user } = useContext(UserContext);

    const getPackGroups = async () => {
        const response = await productionApi.getPackGroupsRecent(user.facilityID);
        if (response.status === false) {
            setNotification({ text: `No entries found ${response.msg}`, level: 'error' });
            return;
        }

        console.log(response.data.rows.length);

        const mmPackGroups = response.data.rows
            .filter((item) => !item.name.includes('ST') && !item.name.includes('LS'))
            .slice(0, 2);

        console.log(mmPackGroups);

        const fbmPackGroups = response.data.rows
            .filter((item) => item.name.includes('ST') || item.name.includes('LS'))
            .slice(0, 2);

        setPackGroups([...mmPackGroups, ...fbmPackGroups]);

        const pgInFacility = response.data.rows.find((pg) => pg.facilityID === user.facilityID);
        if (pgInFacility) {
            setSelectedPackGroup(pgInFacility.id);
        }
    };

    const getPackGroupInventory = async () => {
        setLoading(true);
        setRows([]);
        setPackDateColumns([]);
        const response = await api.getLiveInventoryForPackGroup(selectedPackGroup);
        if (response.status === false) {
            setNotification({ text: `Failed to get inventory for pack group ${response.msg}`, level: 'error' });
            setLoading(false);
            return;
        }

        setPackDateColumns(
            response.data.inventory.packDates.map((item) => ({
                Header: item,
                accessor: `${item}.unitsRemaining`,
            })),
        );
        setRows(response.data.inventory.sellableItems);
        setLoading(false);
    };

    useEffect(() => {
        getPackGroups();
    }, []);

    useEffect(() => {
        // get data
        if (selectedPackGroup) {
            getPackGroupInventory();
        }
    }, [selectedPackGroup]);

    return (
        <div>
            <h1>Live Inventory Units By Pack Date</h1>
            <Notifications options={notification} />

            <NativeSelect
                className="input-element-select"
                value={selectedPackGroup}
                label="Pack Group Select"
                onChange={(event) => {
                    setSelectedPackGroup(event.target.value);
                }}
            >
                {!selectedPackGroup && <option value={selectedPackGroup}>Please Select</option>}
                {packGroups.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                        {`${opt.name} ${opt.week}`}
                    </option>
                ))}
                ;
            </NativeSelect>
            <div>
                {loading ? (
                    <CircularProgress />
                ) : (
                    <div>
                        {rows.length > 0 && packDateColumns.length > 0 ? (
                            <DataTable
                                columns={[
                                    { Header: 'ID', accessor: 'id' },
                                    { Header: 'Name', accessor: 'name' },
                                    { Header: 'Item Type', accessor: 'itemType' },
                                    { Header: 'External Sku', accessor: 'externalSku' },
                                    ...packDateColumns,
                                ]}
                                data={rows}
                                expandable
                                ExpansionComponent={
                                    <ViewLiveInventoryDetails
                                        setNotification={setNotification}
                                        packDateColumns={packDateColumns}
                                    />
                                }
                            />
                        ) : (
                            <div style={{ paddingTop: '8px' }}>No inventory found for this pack group.</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function ViewLiveInventoryDetails({ packDateColumns, parentRow, setNotification }) {
    const [selectedTab, setSelectedTab] = useState(packDateColumns[0].Header);

    const handleChange = (event, value) => {
        setSelectedTab(value);
    };

    return (
        <>
            <h2>Inventory Buckets</h2>
            <TableContainer>
                <Table>
                    <TableBody>
                        <TableRow>
                            <TableCell variant="head">Pack Date</TableCell>
                            <TableCell variant="head">ATS, Not Reserved</TableCell>
                            <TableCell variant="head">On PO, Not Reserved</TableCell>
                            <TableCell variant="head">Reserved</TableCell>
                            <TableCell variant="head">Not ATS</TableCell>
                            <TableCell variant="head">On Hand</TableCell>
                            <TableCell variant="head">Total</TableCell>
                        </TableRow>
                        {packDateColumns.map((item) => {
                            const parentRowData = parentRow.original[item.Header];

                            return (
                                <TableRow key={item.Header}>
                                    {/* Pack Date */}
                                    <TableCell>{item.Header}</TableCell>

                                    {/* ATS, Not Reserved */}
                                    <TableCell>
                                        {parentRow.original.itemType === 'Item Master' ? (
                                            <div>{parentRowData.atsNotReserved.total}</div>
                                        ) : (
                                            <div>
                                                Total: {parentRowData.atsNotReserved.total}
                                                <div>
                                                    Item Level Two Units: {parentRowData.atsNotReserved.itemLevelTwo}
                                                </div>
                                                <div>
                                                    Item Master Units: {parentRowData.atsNotReserved.itemMaster}
                                                    {parentRowData.itemLevelTwoMultiplier > 1 &&
                                                        ` (${parentRowData.itemLevelTwoMultiplier} per Il2)`}
                                                </div>
                                            </div>
                                        )}
                                    </TableCell>

                                    {/* On PO, Not Reserved */}
                                    <TableCell>{parentRowData.onPONotReserved}</TableCell>

                                    {/* Reserved */}
                                    <TableCell>
                                        <>
                                            <div>Total: {parentRowData.reserved.total}</div>
                                            {parentRow.original.itemType === 'Item Master' ? (
                                                <div>From Lots: {parentRowData.reserved.itemMaster}</div>
                                            ) : (
                                                <>
                                                    <div>
                                                        Item Level Two Units: {parentRowData.reserved.itemLevelTwo}
                                                    </div>
                                                    <div>
                                                        Item Master Units: {parentRowData.reserved.itemMaster}
                                                        {parentRowData.itemLevelTwoMultiplier > 1 &&
                                                            ` (${parentRowData.itemLevelTwoMultiplier} per Il2)`}
                                                    </div>
                                                </>
                                            )}
                                            <div>From POs: {parentRowData.reserved.onPO}</div>
                                        </>
                                    </TableCell>

                                    {/* Not ATS */}
                                    <TableCell>
                                        {parentRow.original.itemType === 'Item Master' ? (
                                            <div>{parentRowData.notAts.total}</div>
                                        ) : (
                                            <>
                                                <div>Total: {parentRowData.notAts.total}</div>
                                                <div>Item Level Two Units: {parentRowData.notAts.itemLevelTwo}</div>
                                                <div>
                                                    Item Master Units: {parentRowData.notAts.itemMaster}
                                                    {parentRowData.itemLevelTwoMultiplier > 1 &&
                                                        ` (${parentRowData.itemLevelTwoMultiplier} per Il2)`}
                                                </div>
                                            </>
                                        )}
                                    </TableCell>

                                    {/* On Hand */}
                                    <TableCell>
                                        <div>{parentRowData.onHand}</div>
                                    </TableCell>

                                    {/* Total */}
                                    <TableCell>
                                        <div>{parentRowData.total}</div>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
            <h2>Lots And POs</h2>
            <>
                <Tabs onChange={handleChange} value={selectedTab} indicatorColor="primary" textColor="primary" centered>
                    {packDateColumns.map((item) => (
                        <Tab key={item.Header} label={item.Header} value={item.Header} />
                    ))}
                </Tabs>
                {packDateColumns.map((item) => {
                    const data = parentRow.original[item.Header];
                    const lotsAndPos = [
                        ...data.lots.map((item) => {
                            item.lotID = item.id;
                            item.type = item.externalSku >= 200000 ? 'Item Level Two' : 'Item Master';
                            return item;
                        }),
                        ...data.pos,
                    ];

                    return (
                        <TabPanel selectedTab={selectedTab} index={item.Header} key={item.Header}>
                            <>
                                {lotsAndPos.length > 0 ? (
                                    <DataTable
                                        columns={[
                                            { Header: 'Lot ID', accessor: 'lotID' },
                                            { Header: 'Po ID', accessor: 'poID' },
                                            { Header: 'Type', accessor: 'type' },
                                            {
                                                Header: 'Expires On',
                                                accessor: 'expiresOn',
                                                sortType: 'datetimeString',
                                            },
                                            {
                                                Header: 'Last Packable',
                                                accessor: 'lastPackableDate',
                                                sortType: 'datetimeString',
                                            },
                                            { Header: 'Num Units', accessor: 'numUnits' },
                                            { Header: 'Num Units Reserved', accessor: 'numBaseUnitsClaimed' },
                                        ]}
                                        data={lotsAndPos}
                                        rowStyle={(row) => {
                                            if (!!row.lotID && row.ats == 'f') {
                                                return { backgroundColor: '#ffafaf' };
                                            }
                                            return {};
                                        }}
                                    />
                                ) : (
                                    <div>No data exists for this pack date.</div>
                                )}
                            </>
                        </TabPanel>
                    );
                })}
            </>
        </>
    );
}

export { LiveInventory, ViewLiveInventoryDetails };
